import { Injectable, WritableSignal } from "@angular/core";
import { compile } from "@hsgovtech/hsutils";
import { Location } from "../types/Location/Location";
import { Locations } from "../types/Location/Locations";
import { LocationRepository } from "../repositories/location.repository";
import { HistoryService } from "./history.service";
import { ClientService } from "./client.service";
import { RequestService } from "./request.service";
import { SearchLocation } from "../types/Location/SearchLocation";
import { LogService } from "./log.service";
import { BaseService } from "./base.service";
import { MapService } from "./map.service";
import { LocationListItem } from "../types/Location/LocationListItem";

@Injectable({
  providedIn: "root",
})
export class LocationService extends BaseService {
  constructor(
    private locationRepo: LocationRepository,
    private historyService: HistoryService,
    private clientService: ClientService,
    private requestService: RequestService,
    private mapService: MapService,
    logService: LogService,
  ) {
    super(logService, "LocationService");
  }

  public isDownloaded(locationID: string): boolean {
    const location = this.locationRepo.getById(locationID);
    return !!location;
  }

  public getLocationName(location: Location): string {
    if (location?.output?.name) {
      return location.output.name;
    }
    const currentClient = this.clientService.getCurrentClient();
    if (currentClient) {
      const outputName = currentClient?.clientData?.clientConfig.output.name;
      return outputName ? compile(outputName, location.record) : "";
    }
    this.logger.error("Unable to get location name", {
      location,
      currentClient,
    });
    return "";
  }

  public getLocations(): { [locationID: string]: Location } {
    return this.locationRepo.get();
  }

  public getLocationsAsArray(): Location[] {
    return this.locationRepo.getAsArray();
  }

  public getLocationByID(locationID: string): Location {
    return this.locationRepo.getById(locationID);
  }

  public async removeLocations(
    locations: Location[] | LocationListItem[],
  ): Promise<{ [locationID: string]: Location }> {
    const locationIDs = locations.map((location) => location.locationID);
    await this.mapService.removeStaticMaps(locations);
    return this.locationRepo.removeMultiple(locationIDs);
  }

  public saveLocations(locations: {
    [locationID: string]: Location;
  }): Promise<{ [locationID: string]: Location }> {
    return this.locationRepo.saveRecords(locations);
  }

  public getLocalLocations(): Location[] {
    const locations = this.getLocationsAsArray();
    return locations.filter(
      (location: any) => location.parentFormSource === "Local",
    );
  }

  private createLocations(
    data: any[],
    config: { src: string; pkey: string; formID: string; searchRecords: any[] },
  ): Location[] {
    const searchMap = config.searchRecords.reduce((acc: any, record: any) => {
      return { ...acc, [record.locationID]: record };
    }, {});
    return data.map((row: any) => {
      const searchRecord = searchMap[row[config.pkey]];
      return {
        ...searchRecord,
        record: row,
        inspections: {},
        locationID: row[config.pkey],
        parentFormSource: config.src,
        parentPKey: config.pkey,
        parentFormID: config.formID,
        lat: row.lat,
        lng: row.lng,
        history: {},
        isDownloaded: true,
      };
    });
  }

  private async addHistoryToLocations(
    locations: Location[],
    loadingMessage: WritableSignal<string>,
  ): Promise<{ [locationID: string]: Location }> {
    const history = await this.historyService.downloadHistory(
      loadingMessage,
      locations,
    );
    const updatedLocations = locations.reduce(
      (acc: Locations, location: Location) => {
        return {
          ...acc,
          [location.locationID]: {
            ...location,
            history: history[location.locationID] || {},
          },
        };
      },
      {},
    );
    return updatedLocations;
  }

  private mergeLocations(
    previousLocations: Locations,
    updatedLocations: Locations,
  ): Locations {
    const locations = Object.entries(updatedLocations).map(
      ([key, location]: any) => {
        const previousLocation = previousLocations[location.locationID];
        if (previousLocation) {
          // handle modifications to the location
          const locationModifications: any = Object.values(
            previousLocation.inspections,
          ).reduce((acc: any, inspection: any) => {
            if (inspection.finalized === "1") {
              return { ...acc, ...inspection.locationEdits };
            }
            return acc;
          }, {});
          return [
            key,
            {
              ...location,
              inspections: previousLocation.inspections,
              record: { ...location.record, ...locationModifications },
            },
          ];
        }
        return [key, location];
      },
    );
    return Object.fromEntries(locations);
  }

  public async downloadLocations(
    searchRecords: SearchLocation[],
    loadingMessage: WritableSignal<string>,
    returnAsMap: boolean = false,
  ): Promise<Location[] | { [key: string]: Location } | string> {
    const filteredSearchRecords = searchRecords.filter(
      (record) => record.locationID && record.parentFormSource !== "Local",
    );
    const res = await this.requestService.post("locations/download", {
      locations: filteredSearchRecords,
    });
    if (
      res.response === "Error" ||
      res.status === 401 ||
      res.status === 500 ||
      res.status === 0 ||
      res.success === false
    ) {
      return "Error Downloading Locations";
    }
    const locations = res.results.reduce(
      (
        acc: Location[],
        {
          pkey,
          src,
          formID,
          query,
        }: { pkey: string; src: string; formID: string; query: any },
      ) => {
        return [
          ...acc,
          ...this.createLocations(query, {
            pkey,
            src,
            formID,
            searchRecords,
          }),
        ];
      },
      [],
    );
    const updatedLocations = await this.addHistoryToLocations(
      locations,
      loadingMessage,
    );
    const mergedLocations = this.mergeLocations(
      this.getLocations(),
      updatedLocations,
    );
    await this.saveLocations(mergedLocations);
    await this.mapService.downloadStaticMaps(mergedLocations);
    return returnAsMap ? mergedLocations : Object.values(mergedLocations);
  }
}
